import React, { useState, useEffect } from 'react';
import { dealsAPI, OrderIssue, OrderIssueType, OrderIssueStatus, FulfillmentPriority } from '../../services/api';
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Card, CardBody, Badge, Button, Modal } from '../ui';

interface ProblemsSectionProps {
  dealId: number;
}

const issueTypeLabels: Record<OrderIssueType, string> = {
  DAMAGE: 'Damage',
  SHORTAGE: 'Shortage/Missing Items',
  WRONG_ITEM: 'Wrong Item',
  QUALITY_ISSUE: 'Quality Issue',
  CUSTOMS_DELAY: 'Customs Delay',
  LOST_SHIPMENT: 'Lost Shipment',
  PAYMENT_DISPUTE: 'Payment Dispute',
  RETURN_REQUEST: 'Return Request',
  OTHER: 'Other',
};

const getStatusBadgeVariant = (status: OrderIssueStatus): 'error' | 'warning' | 'success' | 'neutral' => {
  switch (status) {
    case 'OPEN': return 'error';
    case 'INVESTIGATING': return 'warning';
    case 'RESOLVED': return 'success';
    case 'ESCALATED': return 'error';
    case 'CLOSED': return 'neutral';
    default: return 'neutral';
  }
};

const formatStatus = (status: string): string => {
  return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const ProblemsSection: React.FC<ProblemsSectionProps> = ({ dealId }) => {
  const [issues, setIssues] = useState<OrderIssue[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingIssue, setEditingIssue] = useState<OrderIssue | null>(null);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [resolvingIssue, setResolvingIssue] = useState<OrderIssue | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    issueType: 'DAMAGE' as OrderIssueType,
    description: '',
    priority: 'STANDARD' as FulfillmentPriority,
    notes: '',
    refundAmount: '',
  });

  const [resolveData, setResolveData] = useState({
    resolution: '',
    refundAmount: '',
  });

  useEffect(() => {
    fetchIssues();
  }, [dealId]);

  const fetchIssues = async () => {
    try {
      setLoading(true);
      const data = await dealsAPI.getIssues(dealId);
      setIssues(data);
    } catch (error) {
      console.error('Failed to fetch issues:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (issue?: OrderIssue) => {
    if (issue) {
      setEditingIssue(issue);
      setFormData({
        issueType: issue.issueType,
        description: issue.description,
        priority: issue.priority,
        notes: issue.notes || '',
        refundAmount: issue.refundAmount?.toString() || '',
      });
    } else {
      setEditingIssue(null);
      setFormData({
        issueType: 'DAMAGE',
        description: '',
        priority: 'STANDARD',
        notes: '',
        refundAmount: '',
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingIssue(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const data = {
        issueType: formData.issueType,
        description: formData.description,
        priority: formData.priority,
        notes: formData.notes || undefined,
        refundAmount: formData.refundAmount ? parseFloat(formData.refundAmount) : undefined,
      };

      if (editingIssue) {
        await dealsAPI.updateIssue(dealId, editingIssue.id, data);
      } else {
        await dealsAPI.createIssue(dealId, data);
      }

      await fetchIssues();
      handleCloseModal();
    } catch (error) {
      console.error('Failed to save issue:', error);
    }
  };

  const handleDelete = async (issueId: number) => {
    if (!window.confirm('Are you sure you want to delete this problem?')) return;
    
    try {
      await dealsAPI.deleteIssue(dealId, issueId);
      await fetchIssues();
    } catch (error) {
      console.error('Failed to delete issue:', error);
    }
  };

  const handleOpenResolveModal = (issue: OrderIssue) => {
    setResolvingIssue(issue);
    setResolveData({
      resolution: issue.resolution || '',
      refundAmount: issue.refundAmount?.toString() || '',
    });
    setShowResolveModal(true);
  };

  const handleResolve = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resolvingIssue) return;

    try {
      await dealsAPI.resolveIssue(dealId, resolvingIssue.id, {
        resolution: resolveData.resolution,
        refundAmount: resolveData.refundAmount ? parseFloat(resolveData.refundAmount) : undefined,
      });

      await fetchIssues();
      setShowResolveModal(false);
      setResolvingIssue(null);
    } catch (error) {
      console.error('Failed to resolve issue:', error);
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-neutral-900 flex items-center">
          <ExclamationTriangleIcon className="h-5 w-5 mr-2 text-warning-500" />
          Problems & Issues ({issues.length})
        </h3>
        <Button variant="primary" size="sm" onClick={() => handleOpenModal()}>
          <PlusIcon className="h-4 w-4 mr-1" />
          Add Problem
        </Button>
      </div>

      {/* Issues List */}
      {loading ? (
        <div className="text-center py-8 text-neutral-500">Loading...</div>
      ) : issues.length === 0 ? (
        <Card variant="bordered">
          <CardBody className="py-8 text-center text-neutral-500">
            <CheckCircleIcon className="h-12 w-12 mx-auto mb-2 text-success-500" />
            <p>No problems reported for this order</p>
          </CardBody>
        </Card>
      ) : (
        <div className="space-y-3">
          {issues.map((issue) => (
            <Card key={issue.id} variant="bordered" className="hover:shadow-md transition-shadow">
              <CardBody className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant={getStatusBadgeVariant(issue.status)} size="sm">
                        {formatStatus(issue.status)}
                      </Badge>
                      <span className="text-sm font-medium text-neutral-700">
                        {issueTypeLabels[issue.issueType]}
                      </span>
                      {issue.priority !== 'STANDARD' && (
                        <Badge variant="warning" size="sm">
                          {formatStatus(issue.priority)}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-neutral-900 mb-2">{issue.description}</p>
                    {issue.notes && (
                      <p className="text-xs text-neutral-600 mb-2">
                        <strong>Notes:</strong> {issue.notes}
                      </p>
                    )}
                    {issue.resolution && (
                      <p className="text-xs text-success-700 bg-success-50 p-2 rounded mb-2">
                        <strong>Resolution:</strong> {issue.resolution}
                      </p>
                    )}
                    <div className="flex items-center gap-4 text-xs text-neutral-500">
                      <span>Reported: {formatDate(issue.reportedAt)}</span>
                      {issue.resolvedAt && (
                        <span>Resolved: {formatDate(issue.resolvedAt)}</span>
                      )}
                      {issue.refundAmount && (
                        <span className="text-error-600 font-medium">
                          Refund: ${Number(issue.refundAmount).toFixed(2)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    {issue.status !== 'RESOLVED' && issue.status !== 'CLOSED' && (
                      <>
                        <button
                          onClick={() => handleOpenResolveModal(issue)}
                          className="p-1.5 text-success-600 hover:bg-success-50 rounded transition-colors"
                          title="Resolve"
                        >
                          <CheckCircleIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleOpenModal(issue)}
                          className="p-1.5 text-primary-600 hover:bg-primary-50 rounded transition-colors"
                          title="Edit"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => handleDelete(issue.id)}
                      className="p-1.5 text-error-600 hover:bg-error-50 rounded transition-colors"
                      title="Delete"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingIssue ? 'Edit Problem' : 'Add New Problem'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Problem Type *
            </label>
            <select
              value={formData.issueType}
              onChange={(e) => setFormData({ ...formData, issueType: e.target.value as OrderIssueType })}
              className="w-full rounded-lg border-neutral-300 focus:border-primary-500 focus:ring-primary-500"
              required
            >
              {Object.entries(issueTypeLabels).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Description *
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full rounded-lg border-neutral-300 focus:border-primary-500 focus:ring-primary-500"
              rows={3}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Priority
            </label>
            <select
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value as FulfillmentPriority })}
              className="w-full rounded-lg border-neutral-300 focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="STANDARD">Standard</option>
              <option value="URGENT">Urgent</option>
              <option value="RUSH">Rush</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full rounded-lg border-neutral-300 focus:border-primary-500 focus:ring-primary-500"
              rows={2}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Refund Amount ($)
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.refundAmount}
              onChange={(e) => setFormData({ ...formData, refundAmount: e.target.value })}
              className="w-full rounded-lg border-neutral-300 focus:border-primary-500 focus:ring-primary-500"
              placeholder="0.00"
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="ghost" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button type="submit" variant="primary">
              {editingIssue ? 'Update' : 'Add'} Problem
            </Button>
          </div>
        </form>
      </Modal>

      {/* Resolve Modal */}
      <Modal
        isOpen={showResolveModal}
        onClose={() => setShowResolveModal(false)}
        title="Resolve Problem"
      >
        <form onSubmit={handleResolve} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Resolution *
            </label>
            <textarea
              value={resolveData.resolution}
              onChange={(e) => setResolveData({ ...resolveData, resolution: e.target.value })}
              className="w-full rounded-lg border-neutral-300 focus:border-primary-500 focus:ring-primary-500"
              rows={4}
              required
              placeholder="Describe how this problem was resolved..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Refund Amount ($)
            </label>
            <input
              type="number"
              step="0.01"
              value={resolveData.refundAmount}
              onChange={(e) => setResolveData({ ...resolveData, refundAmount: e.target.value })}
              className="w-full rounded-lg border-neutral-300 focus:border-primary-500 focus:ring-primary-500"
              placeholder="0.00"
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="ghost" onClick={() => setShowResolveModal(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="success">
              Mark as Resolved
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ProblemsSection;

